import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiSend,
  FiCheckCircle,
} from "react-icons/fi";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const Contact = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });
      setStatus("success");
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.error || t("common.errorTryAgain"));
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[#f7f0ec] flex flex-col font-raleway">
      {/* ── Hero Banner ── */}
      <div
        className="relative w-full flex items-center justify-center text-white text-center overflow-hidden"
        style={{
          minHeight: "370px",
          backgroundImage: "url('/images/canvasBanner.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 px-6 py-12 max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-[#e8c5b8] mb-3 font-medium">
            Cross Border
          </p>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight">
            &ldquo;{t("footerSection.sentence.s1")}
            <span className="text-[rgb(193,119,112)]">
              {t("footerSection.sentence.s2")}
            </span>
            {t("footerSection.sentence.s3")}&rdquo;
          </h1>
        </div>
      </div>

      {/* ── Contact Section ── */}
      <div className="canvas-background flex-1 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Info */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#83375B] mb-3">
              {t("navbar.contactUs")}
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-[#AA7F96] to-[#83375B] mb-6" />
            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              {t("contactPage.description")}
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#AA7F96]/10 flex items-center justify-center shrink-0">
                  <FiMapPin className="text-[#83375B] text-xl" />
                </div>
                <div>
                  <p className="font-semibold text-[#83375B] mb-0.5">
                    {t("contactPage.location")}
                  </p>
                  <p className="text-gray-600">
                    {t("contactPage.locationValue")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#AA7F96]/10 flex items-center justify-center shrink-0">
                  <FiPhone className="text-[#83375B] text-xl" />
                </div>
                <div>
                  <p className="font-semibold text-[#83375B] mb-0.5">
                    {t("contactPage.phone")}
                  </p>
                  <a
                    href="tel:+905525510595"
                    className="text-gray-600 hover:text-[#83375B] transition-colors"
                  >
                    +90 (552) 551 05 95
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#AA7F96]/10 flex items-center justify-center shrink-0">
                  <FiMail className="text-[#83375B] text-xl" />
                </div>
                <div>
                  <p className="font-semibold text-[#83375B] mb-0.5">
                    {t("contactPage.email")}
                  </p>
                  <a
                    href={`mailto:${process.env.REACT_APP_SALES_EMAIL || "info@crossborder-store.com"}`}
                    className="text-gray-600 hover:text-[#83375B] transition-colors break-all"
                  >
                    {process.env.REACT_APP_SALES_EMAIL ||
                      "info@crossborder-store.com"}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-10 gap-4">
                <FiCheckCircle className="text-6xl text-[#83375B]" />
                <h3 className="text-2xl font-bold text-[#83375B]">
                  {t("contactPage.successTitle")}
                </h3>
                <p className="text-gray-600">
                  {t("contactPage.successMessage")}
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-[#AA7F96] hover:text-[#83375B] font-semibold underline transition-colors"
                >
                  {t("contactPage.sendAnother")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <h3 className="text-xl font-bold text-[#83375B] mb-6">
                  {t("contactPage.formTitle")}
                </h3>

                {/* Name */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-semibold text-gray-700 mb-1.5"
                    htmlFor="name"
                  >
                    {t("contactPage.name")}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t("contactPage.namePlaceholder")}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AA7F96] focus:border-transparent transition"
                  />
                </div>

                {/* Email */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-semibold text-gray-700 mb-1.5"
                    htmlFor="email"
                  >
                    {t("contactPage.email")}{" "}
                    <span className="text-[#83375B]">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("contactPage.emailPlaceholder")}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AA7F96] focus:border-transparent transition"
                  />
                </div>

                {/* Message */}
                <div className="mb-6">
                  <label
                    className="block text-sm font-semibold text-gray-700 mb-1.5"
                    htmlFor="message"
                  >
                    {t("contactPage.message")}{" "}
                    <span className="text-[#83375B]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t("contactPage.messagePlaceholder")}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AA7F96] focus:border-transparent transition resize-y min-h-[130px]"
                  />
                </div>

                {/* Error */}
                {status === "error" && (
                  <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {errorMsg}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full flex items-center justify-center gap-2 bg-[#83375B] hover:bg-[#6d2c4d] text-white font-bold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-[1.02] shadow-md disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {status === "loading" ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                      {t("contactPage.sending")}
                    </>
                  ) : (
                    <>
                      <FiSend className="text-lg" />
                      {t("contactPage.send")}
                    </>
                  )}
                </button>

                {/* reCAPTCHA note */}
                <p className="mt-4 text-xs text-gray-400 text-center leading-relaxed">
                  This site is protected by reCAPTCHA and the Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
